/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXSelector } from '@cloudio-saas/statex';
import { InputAdornment, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { NumberInputProp } from 'componentregistry';
import { helpTextSelector } from 'designer/state';
import useLocalState from 'hooks/useLocalState';
import Icon from 'icons/Icon';
import React, { memo, useState } from 'react';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
  input: {
    '& input, textarea': {
      fontSize: '0.8rem',
    },
  },
});

function TextProperty({
  appUid,
  activeItemId,
  def,
  mgr,
  pageId,
  property,
  value = getDefaultValues<number>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<NumberInputProp, number>) {
  const classes = useStyles();
  const [undoable, setUndoable] = useState(true);
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });

  let val;
  if (value !== Number.MIN_VALUE && value !== Number.MAX_VALUE) {
    val = value;
  }

  const [localValue, setLocalValue] = useLocalState<number | undefined>(
    val,
    (_val) => {
      onChange(property, _val, undoable);
      setUndoable(false);
    },
  );
  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  return (
    <TextField
      disabled={isViewOnlyUser}
      data-testid={property}
      className={classes.root}
      fullWidth
      label={def.label}
      variant="standard"
      required={required}
      value={localValue ?? ''}
      onFocus={(e) => {
        setUndoable(true);
        e.target.select();
      }}
      onBlur={(e) => {
        setUndoable(true);
      }}
      classes={{ root: classes.input }}
      onChange={(e) => {
        try {
          setLocalValue(e.target.value ? Number(e.target.value) : undefined);
        } catch (ex) {
          console.error(ex);
        }
      }}
      InputProps={{
        endAdornment: helpText ? (
          <InputAdornment position="end">
            <Icon
              iconName="comment-exclamation"
              data-tip={helpText}
              data-tip-delay={0}
              data-tip-at="top"
            />
          </InputAdornment>
        ) : undefined,
      }}
      type="number"
    />
  );
}

export default memo(TextProperty);
